import React from "react";

import { Avatar, Box, Stack, Typography } from "@mui/material";

import { Message } from "@twilio/conversations";

import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { UserRole } from "@/types";

import MediaMessage from "./MediaMessage";

interface ChatBubbleProps {
  message: Message;
  isOwnMessage: boolean;
}

const formatMessageTime = (dateCreated: Date | string | null | undefined) => {
  if (!dateCreated) {
    return "";
  }

  const parsedDate =
    dateCreated instanceof Date ? dateCreated : new Date(dateCreated);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleTimeString(undefined, {
    hour: "numeric",
  });
};

const ChatBubble: React.FC<ChatBubbleProps> = ({ message, isOwnMessage }) => {
  const { lesson } = useLesson();
  const { userRole } = useFirebaseContext();

  const participantName =
    userRole === UserRole.Tutor
      ? lesson?.studentName || "Participant"
      : lesson?.tutorName || "Participant";
  const participantPhoto =
    userRole === UserRole.Tutor
      ? lesson?.studentPhotoUrl || ""
      : lesson?.tutorPhotoUrl || "";
  const messageTime = formatMessageTime(message.dateCreated);

  return (
    <Stack alignItems={isOwnMessage ? "flex-end" : "flex-start"} width="100%">
      <Stack direction="row" gap={0.5} alignItems="end">

        {!isOwnMessage ? (
          <Stack height="100%" alignItems="start">

            <Avatar
              alt={participantName}
              src={participantPhoto}
              sx={{
                height: 32,
                mb: 0.5,
                width: 32,
              }}
            >
              {participantName.charAt(0).toUpperCase()}
            </Avatar>
          </Stack>
        ) : null}
        {isOwnMessage && messageTime ? (
          <Typography
            sx={{
              color: "text.secondary",
              flexShrink: 0,
              fontSize: 12,
              lineHeight: "16px",
              mb: 0.75,
            }}
          >
            {messageTime}
          </Typography>
        ) : null}
        <Box
          sx={(theme) => ({
            backgroundColor: isOwnMessage
              ? theme.palette.brand.pink
              : theme.palette.secondary.main,
            borderRadius: 2,
            borderBottomRightRadius: isOwnMessage ? 2 : 8,
            borderTopLeftRadius: !isOwnMessage ? 2 : 8,
            color: isOwnMessage
              ? theme.palette.pink.dark
              : theme.palette.common.white,
            flex: 1,
            maxWidth: {
              xs: 320,
              sm: 395,
            },
            minWidth: 0,
            p: 1.5,
          })}
        >
          {message.type === "media" ? (
            <MediaMessage message={message} />
          ) : (
            <Typography
              variant="text13M"
              sx={(theme) => ({
                color: isOwnMessage ? theme.palette.pink.dark : theme.palette.common.white,
                lineHeight: "20px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              })}
            >
              {message.body}
            </Typography>
          )}
        </Box>
        {!isOwnMessage && messageTime ? (
          <Typography
            variant="text11L"
          >
            {messageTime}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default ChatBubble;
